import React, {useCallback, useLayoutEffect, useMemo, useState} from "react";
import {CircularProgress, TextField} from "@mui/material";
import {Edit} from "@mui/icons-material";
import classnames from "classnames";
import {useTranslation} from "react-i18next";
import {PurchaseHistorySingleItemsDataGridCellKeys} from "../../../../../../../../../core/constants/table/data-grid-column-keys";
import {
    dataGridCellTextProps,
    DataGridColumnAlignments,
    DataGridColumnTypes,
    useDataGridSavedState,
} from "../../../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../../../containers/data-grid";
import IconButton from "../../../../../../../../components/base/button/icon_button";
import {formatMoney} from "../../../../../../../../../core/services/utils/utils";
import PurchaseOrderSerialNumberEntry from "./serial-number-entry";

const saveConfig = {name: 'PurchaseOrderSingleProductsDataGrid', version: 2};

/**
 * Interactive products grid for purchase order single.
 * Qty / cost use data-grid edit popover; serials add/remove call APIs via callbacks.
 */
const PurchaseOrderSingleProductsSection = ({
                                                items = [],
                                                loading = false,
                                                editable = true,
                                                updating = false,
                                                onUpdateOrderQty,
                                                onUpdateCost,
                                                onAddSerial,
                                                onRemoveSerial,
                                            }) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.purchases.orderSingle.products'});

    const columns = useMemo(() => /** @type {DataGridColumn[]} */ ([
        {
            title: t('columns.partNo'),
            name: PurchaseHistorySingleItemsDataGridCellKeys.partNo,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
        },
        {
            title: t('columns.vendorPartNo'),
            name: PurchaseHistorySingleItemsDataGridCellKeys.vendorPartNo,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
        },
        {
            title: t('columns.description'),
            name: PurchaseHistorySingleItemsDataGridCellKeys.description,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            width: 160,
            sortable: false,
        },
        {
            title: t('columns.comments'),
            name: PurchaseHistorySingleItemsDataGridCellKeys.comments,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
        },
        {
            title: t('columns.orderQty'),
            name: PurchaseHistorySingleItemsDataGridCellKeys.orderQty,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.right,
            width: 140,
            sortable: false,
        },
        {
            title: t('columns.cost'),
            name: PurchaseHistorySingleItemsDataGridCellKeys.cost,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.right,
            width: 130,
            sortable: false,
        },
        {
            title: t('columns.extendedCost'),
            name: PurchaseHistorySingleItemsDataGridCellKeys.extendedCost,
            type: DataGridColumnTypes.money,
            alignment: DataGridColumnAlignments.right,
            sortable: false,
        },
        {
            title: t('columns.serials'),
            name: PurchaseHistorySingleItemsDataGridCellKeys.serials,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
            width: 260,
        },
    ]), [t]);

    const [gridColumns, , , density] = useDataGridSavedState({columns, ...saveConfig});

    /**
     * Commits cell edits (order qty / cost) via parent API handlers.
     */
    const onCellEdited = useCallback(async (_, newValue, rowKey, colName) => {
        const item = items.find((row) => row.id === rowKey);
        if (!item) {
            return false;
        }
        switch (colName) {
            case PurchaseHistorySingleItemsDataGridCellKeys.orderQty:
                return onUpdateOrderQty?.(item, newValue);
            case PurchaseHistorySingleItemsDataGridCellKeys.cost:
                return onUpdateCost?.(item, newValue);
            default:
                return false;
        }
    }, [items, onUpdateCost, onUpdateOrderQty]);

    const rows = useMemo(() => items.map((item) => {
        const qtyEditable = editable && !loading && !updating;
        const costEditable = editable && !loading && !updating;
        const extended = item.extendedCost != null
            ? item.extendedCost
            : (Number(item.orderQty) || 0) * (Number(item.cost) || 0);

        return {
            key: item.id,
            data: item,
            cells: {
                [PurchaseHistorySingleItemsDataGridCellKeys.partNo]: item.partNo,
                [PurchaseHistorySingleItemsDataGridCellKeys.vendorPartNo]: item.vendorPartNo,
                [PurchaseHistorySingleItemsDataGridCellKeys.description]: item.description,
                [PurchaseHistorySingleItemsDataGridCellKeys.comments]: item.comments ?? '',
                [PurchaseHistorySingleItemsDataGridCellKeys.orderQty]: {
                    value: ({openEditPopover}) => (
                        <>
                            {
                                qtyEditable &&
                                <IconButton
                                    color={'secondary'}
                                    onPressed={openEditPopover}
                                    disabled={updating}
                                >
                                    {
                                        updating
                                            ? <CircularProgress size={20} color={'secondary'}/>
                                            : <Edit className={'icon small'}/>
                                    }
                                </IconButton>
                            }
                            <p
                                {...dataGridCellTextProps}
                                className={classnames({'ml-2': qtyEditable})}
                            >
                                {item.orderQty ?? 0}
                            </p>
                        </>
                    ),
                    editable: qtyEditable,
                    editableValue: item.orderQty,
                    EditableContentComponent: (
                        <OrderQtyEditor label={t('editors.orderQty')}/>
                    ),
                },
                [PurchaseHistorySingleItemsDataGridCellKeys.cost]: {
                    value: ({openEditPopover}) => (
                        <>
                            {
                                costEditable &&
                                <IconButton
                                    color={'secondary'}
                                    onPressed={openEditPopover}
                                    disabled={updating}
                                >
                                    {
                                        updating
                                            ? <CircularProgress size={20} color={'secondary'}/>
                                            : <Edit className={'icon small'}/>
                                    }
                                </IconButton>
                            }
                            <p
                                {...dataGridCellTextProps}
                                className={classnames({'ml-2': costEditable})}
                            >
                                {formatMoney(item.cost)}
                            </p>
                        </>
                    ),
                    editable: costEditable,
                    editableValue: item.cost ?? 0,
                    EditableContentComponent: (
                        <CostEditor label={t('editors.cost')}/>
                    ),
                },
                [PurchaseHistorySingleItemsDataGridCellKeys.extendedCost]: extended,
                [PurchaseHistorySingleItemsDataGridCellKeys.serials]: (
                    <PurchaseOrderSerialNumberEntry
                        serials={item.serials ?? []}
                        maxCount={Number(item.orderQty) || 0}
                        disabled={!editable || loading || updating}
                        onAdd={(serial) => onAddSerial?.(item, serial)}
                        onRemove={(serial) => onRemoveSerial?.(item, serial)}
                    />
                ),
            },
        };
    }), [
        editable,
        items,
        loading,
        onAddSerial,
        onRemoveSerial,
        t,
        updating,
    ]);

    return (
        <PartnerDataGrid
            {...saveConfig}
            initialLayoutState={{columns}}
            columns={gridColumns}
            rows={rows}
            hideFooterActions
            hideExporting
            state={{loading: {state: loading || updating}, density}}
            onCellEdited={onCellEdited}
        />
    );
};

/**
 * Data-grid popover editor for order qty (> 0).
 */
const OrderQtyEditor = ({value, setValue, setCanSubmitChanges, label}) => {
    /**
     * Validates qty is a finite number greater than zero.
     */
    useLayoutEffect(() => {
        const parsed = Number(value);
        setCanSubmitChanges(!Number.isNaN(parsed) && parsed > 0);
    }, [setCanSubmitChanges, value]);

    /**
     * Parses quantity as a positive integer (or clears when empty).
     */
    const onValueChanged = useCallback((e) => {
        const raw = e.target.value;
        if (!raw || Number.isNaN(Number(raw))) {
            setValue(undefined);
            return;
        }
        setValue(Math.max(0, Math.trunc(Number(raw))));
    }, [setValue]);

    const parsed = Number(value);
    const invalid = Number.isNaN(parsed) || parsed <= 0;

    return (
        <TextField
            fullWidth
            label={label}
            size={'small'}
            value={value?.toString() ?? ''}
            onChange={onValueChanged}
            type={'number'}
            error={invalid}
            inputProps={{min: 1, step: 1}}
            sx={{minWidth: 160}}
        />
    );
};

/**
 * Data-grid popover editor for unit cost (≥ 0).
 */
const CostEditor = ({value, setValue, setCanSubmitChanges, label}) => {
    /**
     * Validates non-negative numeric cost.
     */
    useLayoutEffect(() => {
        const parsed = Number(value);
        setCanSubmitChanges(!Number.isNaN(parsed) && parsed >= 0);
    }, [setCanSubmitChanges, value]);

    /**
     * Parses cost from the text field.
     */
    const onValueChanged = useCallback((e) => {
        const raw = e.target.value;
        if (!raw || Number.isNaN(Number(raw))) {
            setValue(undefined);
            return;
        }
        setValue(Number(raw));
    }, [setValue]);

    return (
        <TextField
            fullWidth
            label={label}
            size={'small'}
            value={value?.toString() ?? ''}
            onChange={onValueChanged}
            type={'number'}
            inputProps={{min: 0, step: 0.01}}
            sx={{minWidth: 160}}
        />
    );
};

export default PurchaseOrderSingleProductsSection;
